import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import CustomButton from './CustomButton';
import CnpjMask from './InputMask/CnpjMask';
import PhoneMask from './InputMask/PhoneMask';
import { useRegister } from '../context/RegisterContext';
import { NegocioData } from '../types/RegisterType';
import './StepNegocio.css';

interface Props {
  onNext: () => void;
  onBack: () => void;
}

const schema = yup.object({
  nomePetshop: yup.string().required('Campo obrigatório'),
  cnpj: yup
    .string()
    .required('Campo obrigatório')
    .test('cnpj', 'CNPJ inválido', val => (val?.replace(/\D/g, '') ?? '').length === 14),
  razaoSocial: yup.string().required('Campo obrigatório'),
  telefone: yup
    .string()
    .required('Campo obrigatório')
    .test('telefone', 'Telefone inválido', val => {
      const digits = val?.replace(/\D/g, '') ?? '';
      return digits.length >= 10 && digits.length <= 11;
    }),
});

const StepNegocio: React.FC<Props> = ({ onNext, onBack }) => {
  const { formData, updateNegocio } = useRegister();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<NegocioData>({
    resolver: yupResolver(schema),
    defaultValues: formData.negocio,
  });

  const onSubmit = (data: NegocioData) => {
    updateNegocio(data);
    onNext();
  };

  return (
    <form className="step-negocio" onSubmit={handleSubmit(onSubmit)}>
      <h2 className="step-negocio-title">Negócio</h2>
      <p className="step-negocio-subtitle">Mostrar o perfil correto no app.</p>

      <div className="form-row form-row-mixed">
        <div className="form-group form-group-grow">
          <label htmlFor="nomePetshop" className="form-label">Nome do Petshop</label>
          <input
            id="nomePetshop"
            className={`form-input ${errors.nomePetshop ? 'error' : ''}`}
            placeholder="Cães e Gatos"
            {...register('nomePetshop')}
          />
          {errors.nomePetshop && <span className="error-message">{errors.nomePetshop.message}</span>}
        </div>

        <div className="form-group form-group-fixed">
          <label htmlFor="cnpj" className="form-label">CNPJ</label>
          <Controller
            name="cnpj"
            control={control}
            render={({ field }) => (
              <CnpjMask
                {...field}
                className={`form-input ${errors.cnpj ? 'error' : ''}`}
                placeholder="00.000.000/0000-00"
              />
            )}
          />
          {errors.cnpj && <span className="error-message">{errors.cnpj.message}</span>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group form-group-full">
          <label htmlFor="razaoSocial" className="form-label">Razão Social</label>
          <input
            id="razaoSocial"
            className={`form-input ${errors.razaoSocial ? 'error' : ''}`}
            placeholder="João e Kaaren de Souza ME"
            {...register('razaoSocial')}
          />
          {errors.razaoSocial && <span className="error-message">{errors.razaoSocial.message}</span>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group form-group-full">
          <label htmlFor="telefone" className="form-label">Telefone</label>
          <Controller
            name="telefone"
            control={control}
            render={({ field }) => (
              <PhoneMask
                {...field}
                className={`form-input ${errors.telefone ? 'error' : ''}`}
                placeholder="(11) 99999-9999"
              />
            )}
          />
          {errors.telefone && <span className="error-message">{errors.telefone.message}</span>}
        </div>
      </div>

      <div className="form-actions form-actions-two">
        <CustomButton
          type="button"
          variant="outlined"
          onClick={onBack}
          sx={{
            borderColor: '#FF914D',
            color: '#FF914D',
            width: '150px',
            '&:hover': {
              borderColor: '#FF914D',
              backgroundColor: 'rgba(255, 145, 77, 0.04)',
              color: '#FF914D',
            },
          }}
        >
          Voltar
        </CustomButton>
        <CustomButton
          type="submit"
          variant="contained"
          customBackground="#46558D"
          sx={{ width: '150px' }}
        >
          Continuar
        </CustomButton>
      </div>
    </form>
  );
};

export default StepNegocio;
